<template>
    <div
        v-if="!$wait.is('loading-page')"
        class="m-blog m-blog__single"
    >
        <y-container>
            <y-page-head
                type="simple"
                :img="post.header_image"
                overlay
            />
            <y-row>
                <y-col md="8">
                    <y-details
                        :published-date="post.published_at"
                        :title="post.title"
                        :synopsis="post.synopsis"
                    />
                    <y-page-content :content="post.text" />
                    <y-tags
                        :title="$t('blog.tags')"
                        :tags-list="post.tagging_tags"
                        route-name="blog-tag"
                    />
                    <y-share
                        :short-link="post.short_link"
                        :title="post.title"
                    />
                    <y-author :author="post.publisher" />
                    <y-comments-list
                        type="simple"
                        :title="$t('blog.comments.list')"
                        :url-encode="$route.params.url_encode"
                        post-type="blog"
                        :button-text="$t('blog.comments.button')"
                        button-link="#r-comments-form"
                        :scroll-button="true"
                        :comments-per-page="5"
                        :pagination="true"
                    />
                    <y-comments-form
                        type="metro"
                        :title="$t('blog.comments.form')"
                        color="primary"
                        :post-id="post_id"
                        :configs="configs"
                    />
                </y-col>
                <y-col
                    md="4"
                    class="m-blog__single__banners mt-6"
                >
                    <aside>
                        <y-row>
                            <y-col
                                xs="12"
                                sm="6"
                                md="12"
                            >
                                <div
                                    v-if="categories && categories.length"
                                    class="m-blog__single__banners-category mb-4 pl-6 pr-6 pt-4"
                                >
                                    <header class="typo-h4 mb-4">
                                        {{ $t('blog.category') }}
                                    </header>
                                    <div
                                        v-for="cat in categories"
                                        :key="cat.id"
                                    >
                                        <y-card
                                            type="banner"
                                            tag="router-link"
                                            height="83px"
                                            :title="cat.title"
                                            :src="get(cat, 'image.0')"
                                            :to="{ name: 'blog-category', params: { url_encode: cat.url_encode }}"
                                            class="pb-4"
                                        />
                                    </div>
                                </div>
                            </y-col>
                            <y-col
                                xs="12"
                                sm="6"
                                md="12"
                            >
                                <y-card
                                    v-if="$settings('content.auth-image')"
                                    type="banner"
                                    tag="router-link"
                                    height="624px"
                                    :title="$t('blog.adv.title')"
                                    :subtitle="$t('blog.adv.subtitle')"
                                    :src="$settings('content.auth-image')"
                                    :to="{ name: 'products-list'}"
                                    class="pb-4"
                                    :button-content="$t('blog.adv.button_text')"
                                    color="primary"
                                />
                            </y-col>
                        </y-row>
                    </aside>
                </y-col>
            </y-row>
            <section>
                <y-related
                    :posts="relatedPosts"
                    :title="$t('blog.related.title')"
                    :link-text="$t('blog.related.button_text')"
                    :link="{ name: 'blog-list' }"
                />
            </section>
        </y-container>
    </div>
</template>

<script>
    import PageMixin from '@nodes/mixins/Page';
    import { YPageContent } from '@components';
    import {
        YRelated,
        YAuthor,
        YShare,
        YTags,
        YDetails,
    } from '@modules/blog/block/index';
    import {
        YCard,
        YPageHead,
        YCommentsList,
        YCommentsForm,
    } from '@rogues/index';
    
    export default {
        name: 'Single',

        components: {
            YPageHead,
            YCommentsList,
            YCommentsForm,
            YDetails,
            YTags,
            YPageContent,
            YShare,
            YAuthor,
            YRelated,
            YCard,
        },

        mixins: [PageMixin],

        /**
         * @inheritdoc
         */
        data() {
            return {
                configs     : {},
                post        : null,
                page_head   : null,
                recaptcha   : {},
                post_id     : null,
                relatedPosts: [],
                categories  : null,
            };
        },

        /**
         * @inheritDoc
         */
        metaInfo() {
            return {
                title: this.get(this.post, 'title', this.$t('blog.title')),
            };
        },

        methods: {
            /**
             * Fetch data
             * 
             * @returns {Promise}
             */
            fetch() {
                const postSingle = this.$services.Content.postSingle({
                    type          : 'blog',
                    slug          : this.$route.params.url_encode,
                    including     : ['id', 'text', 'title', 'image', 'header_image', 'synopsis', 'published_at', 'publisher', 'tagging_tags', 'short_link', 'published_by', 'similar_posts_by_tagging', 'url_encode'],
                    criteria_token: this.$route.query.criteria_token,
                }).catch((error) => {
                    this.handleError(error);
                });

                const category = this.$services.Tagging.labels({
                    slug     : 'blog-category',
                    including: ['image', 'title', 'slug', 'url_encode'],
                }).catch((error) => {
                    this.handleError(error);
                });

                const forms = this.$services.Commenting.getFormConfig({
                    slug: this.$route.params.url_encode,
                    type: 'blog',
                }).catch((error) => {
                    this.handleError(error);
                });
                return Promise.all([postSingle, forms, category]).then((responses) => {
                    if (this.handleReq(responses, 0)) {
                        this.post = responses[0].data.results;
                        this.post_id = this.get(responses[0].data.results, 'id');
                        this.relatedPosts = responses[0].data.results.similar_posts_by_tagging.slice(0, 3);
                    }
                    if (this.handleReq(responses, 1)) {
                        this.configs = {
                            ...responses[1].data.results,
                            recaptcha: responses[1].data.metadata.recaptcha,
                        };
                    }
                    if (this.handleReq(responses, 2)) {
                        this.categories = responses[2].data.results;
                    }
                });
            },
        },
    };
</script>
